// Shared types for AI Agent Waterfall System

export type AgentType =
  | "orchestrator"
  | "researcher"
  | "analyzer"
  | "writer"
  | "executor";

export type WorkflowStatus =
  | "idle"
  | "running"
  | "paused"
  | "completed"
  | "failed"
  | "cancelled";

export type AgentStatus =
  | "idle"
  | "assigned"
  | "processing"
  | "complete"
  | "error";

export type EventType =
  | "message"
  | "toolCall"
  | "toolResult"
  | "statusChange"
  | "error";

export interface WorkflowConfig {
  name: string;
  description: string;
  objective: string;
  maxDuration?: number;
  agentTypes?: AgentType[];
  parallelExecution?: boolean;
  autoStart?: boolean;
}

export interface Workflow {
  id: string;
  name: string;
  description: string;
  objective: string;
  status: WorkflowStatus;
  config: WorkflowConfig;
  createdAt: string;
  updatedAt: string;
  startedAt?: string;
  completedAt?: string;
  userId: string;
  totalEvents: number;
  duration?: number;
}

export interface Agent {
  id: string;
  workflowId: string;
  type: AgentType;
  objective: string;
  status: AgentStatus;
  createdAt: string;
  updatedAt: string;
  metadata?: Record<string, any>;
}

export interface Event {
  id: string;
  workflowId: string;
  agentId: string;
  type: EventType;
  timestamp: number;
  content: string;
  status: string;
  toolName?: string;
  metadata?: Record<string, any>;
  duration?: number;
  parentEventId?: string;
}

export interface WorkflowSummary {
  workflow: Workflow;
  agents: Agent[];
  eventCount: number;
  lastActivity: string;
}

// WebSocket message types
export type WebSocketMessageType =
  | "workflow.started"
  | "workflow.completed"
  | "workflow.failed"
  | "workflow.paused"
  | "workflow.resumed"
  | "agent.spawned"
  | "agent.completed"
  | "agent.failed"
  | "event.created"
  | "error.occurred"
  | "heartbeat"
  | "subscribe"
  | "unsubscribe";

export interface WebSocketMessage {
  type: WebSocketMessageType;
  data?: any;
  timestamp: number;
  workflowId?: string;
  error?: string;
}

export interface WorkflowCreateRequest {
  name: string;
  description: string;
  objective: string;
  config?: Partial<WorkflowConfig>;
}

export interface WorkflowStartRequest {
  workflowId: string;
  userId: string;
}

export interface APIResponse<T = any> {
  success: boolean;
  data?: T;
  error?: string;
  timestamp: number;
}

export interface PaginationParams {
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
}

export interface WorkflowListResponse {
  workflows: WorkflowSummary[];
  total: number;
  page: number;
  limit: number;
  hasMore: boolean;
}

export interface EventListResponse {
  events: Event[];
  total: number;
  page: number;
  limit: number;
  hasMore: boolean;
}

// Agent-specific objective templates
export const AGENT_OBJECTIVES: Record<AgentType, string> = {
  orchestrator: "Coordinate all agents and manage workflow execution",
  researcher:
    "Gather comprehensive data from multiple sources and validate information accuracy",
  analyzer:
    "Identify patterns, trends, and insights using statistical analysis and data processing",
  writer:
    "Create professional documents, reports, and content with proper structure and clarity",
  executor:
    "Generate visualizations, charts, and interactive elements to present findings",
};

// Color schemes for visualization
export const AGENT_COLORS: Record<AgentType, string> = {
  orchestrator: "#8b5cf6",
  researcher: "#06b6d4",
  analyzer: "#10b981",
  writer: "#f59e0b",
  executor: "#ef4444",
};

export const EVENT_COLORS: Record<EventType, string> = {
  message: "#6b7280",
  toolCall: "#3b82f6",
  toolResult: "#10b981",
  statusChange: "#8b5cf6",
  error: "#ef4444",
};

// Validation helpers
export function isValidAgentType(type: string): type is AgentType {
  return [
    "orchestrator",
    "researcher",
    "analyzer",
    "writer",
    "executor",
  ].includes(type);
}

export function isValidWorkflowStatus(
  status: string,
): status is WorkflowStatus {
  return [
    "idle",
    "running",
    "paused",
    "completed",
    "failed",
    "cancelled",
  ].includes(status);
}

export function isValidEventType(type: string): type is EventType {
  return [
    "message",
    "toolCall",
    "toolResult",
    "statusChange",
    "error",
  ].includes(type);
}

// Error types
export class WorkflowError extends Error {
  constructor(
    message: string,
    public code: string,
    public workflowId?: string,
    public agentId?: string,
  ) {
    super(message);
    this.name = "WorkflowError";
  }
}

export class AgentError extends Error {
  constructor(
    message: string,
    public code: string,
    public agentId: string,
    public workflowId?: string,
  ) {
    super(message);
    this.name = "AgentError";
  }
}

// Constants
export const MAX_WORKFLOW_DURATION = 300; // 5 minutes in seconds
export const MAX_EVENTS_PER_WORKFLOW = 1000;
export const MAX_CONCURRENT_WORKFLOWS_PER_USER = 10;
export const WEBSOCKET_HEARTBEAT_INTERVAL = 30000; // 30 seconds
export const EVENT_BATCH_SIZE = 10;
export const DEFAULT_PAGE_SIZE = 20;
